import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { accessState, trialMsLeft, formatLeft, isTrial, isLocked } from '../access';
import { Link, NavLink } from 'react-router-dom';
import './shell.css';

/* Session storage can throw in locked-down browsers — never let the rail die for it. */
function ss(key) {
  try {
    return sessionStorage.getItem(key) || '';
  } catch (e) {
    return '';
  }
}

const IconDashboard = (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M3 13h8V3H3zM13 21h8V11h-8zM3 21h8v-5H3zM13 8h8V3h-8z" />
  </svg>
);

const IconHeart = (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M12 21s-7-4.5-7-9.5A3.5 3.5 0 0 1 12 9a3.5 3.5 0 0 1 7 2.5c0 5-7 9.5-7 9.5z" />
  </svg>
);

const IconPlayers = (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="9" cy="8" r="3" />
    <path d="M3 20c0-3 2.7-5 6-5s6 2 6 5" />
    <path d="M17 11h4M17 15h4" />
  </svg>
);

const IconLists = (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />
  </svg>
);

const IconField = (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 3v18M3 12h18M6 5.5c2.5 2 2.5 11 0 13M18 5.5c-2.5 2-2.5 11 0 13" />
  </svg>
);

const IconInbox = (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M4 5h16v11H8l-4 4z" />
    <path d="M8 9.5h8M8 12.5h5" />
  </svg>
);

const IconPlans = (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="M3 10h18M7 15h4" />
  </svg>
);

const IconProfile = (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="12" cy="8" r="3.5" />
    <path d="M4.5 20c0-3.6 3.4-6 7.5-6s7.5 2.4 7.5 6" />
  </svg>
);

const GROUPS = [
  {
    label: 'Scouting',
    items: [
      { to: '/dashboard', text: 'Dashboard', icon: IconDashboard },
      { to: '/favouriteplayers', text: 'Favourite Players', icon: IconHeart },
      { to: '/playerslist', text: 'Players List', icon: IconPlayers },
      { to: '/lists', text: "Players' Lists", icon: IconLists }
    ]
  },
  {
    label: 'Messages',
    items: [{ to: '/inbox', text: 'Inbox', icon: IconInbox, badge: 'unread' }]
  },
  {
    label: 'Court',
    items: [{ to: '/field', text: 'Field', icon: IconField }]
  },
  {
    label: 'Account',
    items: [
      { to: '/plans', text: 'Plans', icon: IconPlans },
      { to: '/profile', text: 'Profile', icon: IconProfile }
    ]
  }
];

/**
 * `compact` is decided by AppShell from the current route. In compact mode the
 * rail is an icon-only strip: the labels are removed from the accessibility
 * tree along with the text, so every item carries its own aria-label, and a
 * title attribute gives the pointer user the same name as a tooltip.
 */
/*
 * Unread messages from players, for the Inbox badge. Asked once when the rail
 * mounts and then every minute; the Inbox page also announces the number
 * whenever it reloads, so the badge follows what the scout is reading. The
 * last value is kept for the session so the badge does not blink on every
 * page change. Trials cannot message, so they never ask.
 */
const UNREAD_MS = 60000;

function useUnread() {
  const [n, setN] = useState(() => Number(ss('inboxUnread')) || 0);

  useEffect(() => {
    if (isTrial() || isLocked()) return undefined;
    let alive = true;
    const keep = (v) => {
      const num = Number(v) || 0;
      if (!alive) return;
      setN(num);
      try { sessionStorage.setItem('inboxUnread', String(num)); } catch (e) {}
    };
    const ask = () => {
      axios
        .get(process.env.REACT_APP_API_SERVER + 'manager/messages/unread', {
          headers: { Authorization: 'Bearer ' + ss('mngapiToken') }
        })
        .then((res) => keep(res.data && res.data.numOfMessages))
        .catch(() => {});
    };
    const onAnnounce = (e) => keep(e.detail);
    ask();
    const t = setInterval(ask, UNREAD_MS);
    window.addEventListener('inbox-unread', onAnnounce);
    return () => {
      alive = false;
      clearInterval(t);
      window.removeEventListener('inbox-unread', onAnnounce);
    };
  }, []);

  return n;
}

export default function Sidebar({ compact }) {
  const unread = useUnread();
  const planName = ss('ProductTitle') || 'No active plan';
  const planEnd = ss('ProductEndDate');
  /* A trial is labelled as one, and an ended trial says so — the card
     used to read 'Subscription active' for both. */
  const access = accessState();
  const planNote = access === 'trial_expired'
    ? 'Free trial ended'
    : access === 'trial'
      ? 'Free trial · ' + formatLeft(trialMsLeft()) + ' left'
      : planEnd ? 'Until ' + planEnd : 'Subscription active';
  const isCompact = compact === true;

  return (
    <aside className={isCompact ? 'rail rail-compact' : 'rail'}>
      <Link
        className="brand-link"
        to="/dashboard"
        aria-label="123High — dashboard"
        title={isCompact ? '123High' : undefined}
      >
        <img className="brand-img" src="/logo.png" alt="123High" />
        <div className="brand-text">
          <div className="brand-name">123High</div>
          <div className="brand-sub">onetwothreehigh.com</div>
        </div>
      </Link>

      <nav className="nav" aria-label="Main">
        {GROUPS.map((group, gi) => (
          <React.Fragment key={group.label}>
            <div className={gi === 0 ? 'mono nav-label' : 'mono nav-label nav-gap'}>
              {group.label}
            </div>
            {group.items.map((item) => (
              <NavLink
                key={item.to}
                className="nav-item"
                to={item.to}
                aria-label={item.badge && unread > 0 ? item.text + ', ' + unread + ' unread' : item.text}
                title={isCompact ? item.text : undefined}
              >
                {item.icon}
                <span className="nav-text">{item.text}</span>
                {item.badge && unread > 0 ? (
                  <span className="nav-badge" aria-hidden="true">{unread > 99 ? '99+' : unread}</span>
                ) : null}
              </NavLink>
            ))}
          </React.Fragment>
        ))}
      </nav>

      <div className="rail-foot">
        <div className="plan">
          <div className="plan-row">
            <span className="plan-name" title={planName}>{planName}</span>
          </div>
          <div className="plan-bar" aria-hidden="true"><i style={{ width: '100%' }} /></div>
          <div className="plan-until">{planNote}</div>
        </div>
      </div>
    </aside>
  );
}
